<template>
  <div>
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ $t("MENU.ITEM.SERVICE.LOCATIONS") }}
            <i class="mr-2"></i>
            <small>{{ $t("LABELS.LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar">
          <router-link
            v-permission="['service-locations-create']"
            to="add"
            class="btn btn-light-success font-weight-bolder ml-2"
          >
            <i class="flaticon2-plus"></i>
            {{ $t("LABELS.ADD_NEW") }}
          </router-link>
        </div>
      </template>
      <v-card v-show="!isPageLoading">
        <ResponsiveFilterSheet
          :filters="filters"
          :searchQuery.sync="searchQuery"
          :show-filters="false"
          @search="doSearch"
          @clear="doClear"
        >
          <template v-slot:desktop>
            <SearchTools
              :searchQuery.sync="searchQuery"
              @do-search="doSearch"
              @do-clear="doClear"
            >
              <template v-slot:prepend></template>
            </SearchTools>
          </template>
        </ResponsiveFilterSheet>
        <ResponsiveDataTable
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          :loading="isLoadingServiceLocation"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right'
          }"
          class="elevation-1"
          @update:options="getDataFromApi"
          :mobile-summary-fields="mobileSummaryFields"
          :mobile-detail-fields="mobileDetailFields"
          :mobile-toggle-label="$t('LABELS.DETAILS')"
        >
          <template v-slot:[`item.serviceLocationType`]="{ item }">
            {{ serviceLocationTypeName(item) }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <router-link
              v-if="checkIfUserHasPermission(['service-locations-update'])"
              :to="{ name: 'edit-service-locations', params: { id: item.id } }"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mr-2" fab dark x-small v-bind="attrs" v-on="on">
                    <v-icon dark>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("LABELS.EDIT") }}</span>
              </v-tooltip>
            </router-link>
            <template v-else>{{ $t("LABELS.NO_PERMISSIONS") }}</template>
          </template>
          <template v-slot:mobile-summary-actions="{ item }">
            <v-btn
              v-if="checkIfUserHasPermission(['service-locations-update'])"
              :to="{ name: 'edit-service-locations', params: { id: item.id } }"
              :aria-label="$t('LABELS.EDIT')"
              dark
              fab
              x-small
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              Your search for "{{ searchQuery }}" found no results.
            </v-alert>
          </template>
        </ResponsiveDataTable>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      />
    </b-card>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { mapGetters } from "vuex";
import SearchTools from "@/core/components/table/SearchTools.vue";
import ResponsiveDataTable from "@/core/components/table/ResponsiveDataTable.vue";
import ResponsiveFilterSheet from "@/core/components/table/ResponsiveFilterSheet.vue";
import { FETCH_SERVICE_LOCATIONS } from "@/modules/service-locations/store/service-locations.module";
import { permissionMixin } from "@/core/mixins/permissionMixin.js";

export default {
  mixins: [permissionMixin],
  components: {
    SearchTools,
    ResponsiveDataTable,
    ResponsiveFilterSheet
  },
  data() {
    return {
      isPageLoading: true,
      items: [],
      totalItems: 100,
      searchQuery: "",
      options: {
        sortBy: ["name"],
        sortDesc: [false],
        page: 1,
        itemsPerPage: 30,
        totalItems: 100
      },
      filters: {},
      rowsPerPageItems: [10, 20, 30, 40, 50],
      headers: [
        {
          text: this.$i18n.t("FORM_LABELS.SERVICE_LOCATION_TYPE"),
          value: "serviceLocationType",
          sortable: false
        },
        { text: this.$i18n.t("FORM_LABELS.NAME"), value: "name" },
        { text: this.$i18n.t("FORM_LABELS.LOCATION"), value: "location" },
        {
          align: "right",
          text: this.$i18n.t("MENU.ITEM.ACTIONS"),
          value: "actions",
          sortable: false,
          width: "10%"
        }
      ]
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.SERVICE.LOCATIONS"),
        route: { name: "list-service-locations" }
      },
      { title: this.$i18n.t("MENU.ITEM.ASSETS.LIST") }
    ]);
  },
  computed: {
    ...mapGetters(["isLoadingServiceLocation"]),
    params() {
      return {
        ...this.options,
        ...this.filters,
        query: this.searchQuery
      };
    },
    tableHeaders() {
      return this.headers;
    },
    mobileSummaryFields() {
      return [
        {
          key: "name",
          label: this.$t("FORM_LABELS.NAME"),
          primary: true
        },
        {
          key: "serviceLocationType",
          label: this.$t("FORM_LABELS.SERVICE_LOCATION_TYPE"),
          value: item => this.serviceLocationTypeName(item) || "-"
        }
      ];
    },
    mobileDetailFields() {
      return [
        {
          key: "location",
          label: this.$t("FORM_LABELS.LOCATION")
        }
      ];
    }
  },
  methods: {
    doSearch() {
      if (this.options.page === 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },
    doClear() {
      this.searchQuery = "";
      if (this.options.page === 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },
    getDataFromApi() {
      const params = this.params;
      params.length = params.itemsPerPage;
      params.start =
        params.page === 1 ? 0 : params.itemsPerPage * (params.page - 1);

      this.$store
        .dispatch(FETCH_SERVICE_LOCATIONS, this.$url.transformParams(params))
        .then(data => {
          this.items = data.data.items;
          this.totalItems = data.data.totalItemsCount;
          this.isPageLoading = false;
        });
    },
    serviceLocationTypeName(item) {
      const type = item.serviceLocationType;
      return type ? type.name : "";
    }
  }
};
</script>
